
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ImageUploadProps {
  currentImages?: string[];
  bucketName: string;
  onUploadComplete: (urls: string[]) => void;
  maxFileSize?: number; // in MB
  allowMultiple?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  className?: string;
  buttonText?: string;
  filePrefix?: string;
}

export const ImageUpload = ({
  currentImages = [],
  bucketName,
  onUploadComplete,
  maxFileSize = 5, // Default max 5MB
  allowMultiple = false,
  maxWidth = 1920,
  maxHeight = 1080,
  className = "",
  buttonText = "Upload Image",
  filePrefix = "",
}: ImageUploadProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newImages, setNewImages] = useState<string[]>([...currentImages]);

  const compressImage = useCallback(async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if needed
          if (width > maxWidth) {
            height = Math.round(height * maxWidth / width);
            width = maxWidth;
          }
          
          if (height > maxHeight) {
            width = Math.round(width * maxHeight / height);
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            // Create new file from the blob
            const optimizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            resolve(optimizedFile);
          }, 'image/jpeg', 0.85); // Adjust quality here (0.85 = 85% quality)
        };
      };
      reader.onerror = () => reject(new Error('FileReader error'));
    });
  }, [maxWidth, maxHeight]);

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return `File type not supported. Please upload ${validTypes.map(t => t.split('/')[1]).join(', ')}`;
    }
    
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxFileSize) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }
    
    return null;
  }, [maxFileSize]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    const uploadedUrls: string[] = [...newImages];
    const fileArray = allowMultiple ? Array.from(files) : [files[0]];
    
    try {
      let filesProcessed = 0;
      
      for (const file of fileArray) {
        // Validate file before processing
        const validationError = validateFile(file);
        if (validationError) {
          toast({
            title: "Validation Error",
            description: validationError,
            variant: "destructive",
          });
          continue;
        }
        
        // Try to compress the image
        let fileToUpload: File;
        try {
          fileToUpload = await compressImage(file);
          console.log(`Compressed ${file.name} from ${file.size} to ${fileToUpload.size} bytes`);
        } catch (compressionError) {
          console.warn("Image compression failed, using original file:", compressionError);
          fileToUpload = file;
        }
        
        // Create a unique filename
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `${filePrefix}${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from(bucketName)
          .upload(filePath, fileToUpload, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error details:", uploadError);
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        
        // Update progress
        filesProcessed++;
        setUploadProgress(Math.round((filesProcessed / fileArray.length) * 100));
      }

      setNewImages(uploadedUrls);
      onUploadComplete(uploadedUrls);
      
      toast({
        title: "Success",
        description: `${uploadedUrls.length - newImages.length} image(s) uploaded successfully`,
      });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset the input value to allow uploading the same file again
      if (e.target) e.target.value = '';
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById('file-upload')?.click()}
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              {buttonText}
            </>
          )}
        </Button>
        <input
          id="file-upload"
          type="file"
          accept="image/*"
          multiple={allowMultiple}
          className="hidden"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
        
        {isUploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">{uploadProgress}%</p>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Supported formats: JPEG, PNG, GIF, WebP. Max size: {maxFileSize}MB.
        </p>
      </div>
    </div>
  );
};
